import React, { useEffect, useMemo, useState } from 'react';
import { PageHeader, Btn, Card, Modal, Input, Select, Empty, Spinner, Badge, useToast } from '../../components/shared/UI';
import PasswordStrength, { checkPassword } from '../../components/shared/PasswordStrength';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';

const empty = { name: '', email: '', password: '', role: 'user' };

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm]       = useState(empty);
  const [saving, setSaving]   = useState(false);
  const [busyId, setBusyId]   = useState('');
  const [selected, setSelected]       = useState([]);
  const [extendModal, setExtendModal] = useState(false);
  const [extendDays, setExtendDays]   = useState('30');
  const [extendScope, setExtendScope] = useState('selected');
  const [extending, setExtending]     = useState(false);
  const [historyUser, setHistoryUser] = useState(null);
  const [subUser, setSubUser] = useState(null);
  const [subData, setSubData] = useState(null);
  const [broadcastModal, setBroadcastModal] = useState(false);
  const [broadcast, setBroadcast] = useState({ title: '', message: '', sendEmail: false });
  const [broadcasting, setBroadcasting] = useState(false);
  const [search, setSearch]             = useState('');
  const [filterRole, setFilterRole]     = useState('');
  const [filterPlan, setFilterPlan]     = useState('');
  const [filterActive, setFilterActive] = useState('');
  const [filterVerified, setFilterVerified] = useState('');
  const [sortKey, setSortKey] = useState('registered');
  const [sortDir, setSortDir] = useState('desc');
  const { show, ToastContainer } = useToast();

  const load = () => api.get('/admin/users').then(r => setUsers(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const openNew  = () => { setEditing(null); setForm(empty); setModal(true); };
  const openEdit = u => {
    setEditing(u.id);
    setForm({
      name: u.name, email: u.email, password: '', role: u.role,
      plan: u.plan, subscriptionStatus: u.subscriptionStatus,
      isActive: u.isActive, emailVerified: u.emailVerified,
    });
    setModal(true);
  };

  const set    = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const setBool = k => e => setForm(f => ({ ...f, [k]: e.target.checked }));

  const save = async e => {
    e.preventDefault();
    if (form.password && !checkPassword(form.password).valid) {
      show('Password does not meet the security requirements.', 'error');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        const { data } = await api.put(`/admin/users/${editing}`, payload);
        setUsers(us => us.map(u => u.id === editing ? data : u));
        show('User updated');
      } else {
        const { data } = await api.post('/admin/users', form);
        setUsers(us => [data, ...us]);
        show('User created');
      }
      setModal(false);
    } catch (err) {
      show(err.response?.data?.message || 'Save failed', 'error');
    } finally { setSaving(false); }
  };

  const verify = async u => {
    setBusyId(u.id);
    try {
      const { data } = await api.post(`/admin/users/${u.id}/verify`);
      setUsers(us => us.map(x => x.id === u.id ? data : x));
      show(`${u.name} verified`);
    } catch (err) {
      show(err.response?.data?.message || 'Verify failed', 'error');
    } finally { setBusyId(''); }
  };

  const toggleActive = async u => {
    const action = u.isActive ? 'Deactivate' : 'Activate';
    if (!window.confirm(`${action} ${u.name}?`)) return;
    setBusyId(u.id);
    try {
      const { data } = await api.post(`/admin/users/${u.id}/toggle-active`);
      setUsers(us => us.map(x => x.id === u.id ? data : x));
      show(`${u.name} ${data.isActive ? 'activated' : 'deactivated'}`);
    } catch (err) {
      show(err.response?.data?.message || 'Failed', 'error');
    } finally { setBusyId(''); }
  };

  // Support rescue: user lost their authenticator → admin turns their 2FA off
  const reset2fa = async u => {
    if (!window.confirm(`Turn off two-factor authentication for ${u.name}?\n\nOnly do this after verifying the request really comes from them.`)) return;
    setBusyId(u.id);
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-2fa`);
      setUsers(us => us.map(x => x.id === u.id ? data : x));
      show(`2FA turned off for ${u.name}`);
    } catch (err) {
      show(err.response?.data?.message || 'Failed', 'error');
    } finally { setBusyId(''); }
  };

  // ── Search / filter / sort ────────────────────────────────────────────────
  const SORT_VALUES = {
    name:       u => (u.name || '').toLowerCase(),
    email:      u => (u.email || '').toLowerCase(),
    role:       u => u.role,
    plan:       u => u.plan,
    trialEnds:  u => (u.plan === 'trial' && u.trialEndsAt) ? new Date(u.trialEndsAt).getTime() : Infinity,
    registered: u => u.createdAt ? new Date(u.createdAt).getTime() : 0,
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = users.filter(u =>
      (!q || (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q)) &&
      (!filterRole || u.role === filterRole) &&
      (!filterPlan || u.plan === filterPlan) &&
      (!filterActive || String(!!u.isActive) === filterActive) &&
      (!filterVerified || String(!!u.emailVerified) === filterVerified)
    );
    const getVal = SORT_VALUES[sortKey] || SORT_VALUES.registered;
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      const va = getVal(a), vb = getVal(b);
      if (va < vb) return -dir;
      if (va > vb) return dir;
      return 0;
    });
  }, [users, search, filterRole, filterPlan, filterActive, filterVerified, sortKey, sortDir]);

  const filtersActive = search.trim() || filterRole || filterPlan || filterActive || filterVerified;
  const clearFilters = () => {
    setSearch(''); setFilterRole(''); setFilterPlan(''); setFilterActive(''); setFilterVerified('');
  };

  const sortBy = key => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir(key === 'registered' ? 'desc' : 'asc'); }
  };
  const SortTh = ({ k, children, align }) => (
    <th style={{ ...th, textAlign: align || 'left', cursor: 'pointer', userSelect: 'none' }}
        onClick={() => sortBy(k)} title="Sort">
      {children}{sortKey === k ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
    </th>
  );

  const toggleSelect = id => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  // Select-all applies to the FILTERED list, so filter → select all → bulk-extend works
  const toggleSelectAll = () => setSelected(s =>
    visible.every(u => s.includes(u.id)) ? [] : visible.map(u => u.id));

  const openExtend = () => {
    setExtendScope(selected.length ? 'selected' : 'all');
    setExtendDays('30');
    setExtendModal(true);
  };

  const extendTrial = async e => {
    e.preventDefault();
    setExtending(true);
    try {
      const payload = extendScope === 'all'
        ? { all: true, days: extendDays }
        : { userIds: selected, days: extendDays };
      const { data } = await api.post('/admin/users/extend-trial', payload);
      const updated = new Map(data.users.map(u => [u.id, u]));
      setUsers(us => us.map(u => updated.get(u.id) || u));
      show(`Trial extended by ${extendDays} day${extendDays !== '1' ? 's' : ''} for ${data.updated} user${data.updated !== 1 ? 's' : ''}`);
      setExtendModal(false);
      setSelected([]);
    } catch (err) {
      show(err.response?.data?.message || 'Extend failed', 'error');
    } finally { setExtending(false); }
  };

  // Support session: park the admin token, swap to a 1-hour token for the
  // target user, and reload. Layout shows a banner with a "return" button.
  const impersonate = async u => {
    if (!window.confirm(`Sign in as ${u.name}? Your actions will be recorded in the activity log.`)) return;
    setBusyId(u.id);
    try {
      const { data } = await api.post(`/admin/users/${u.id}/impersonate`);
      localStorage.setItem('adminToken', localStorage.getItem('token'));
      localStorage.setItem('token', data.token);
      window.location.href = '/';
    } catch (err) {
      show(err.response?.data?.message || 'Impersonation failed', 'error');
      setBusyId('');
    }
  };

  const sendBroadcast = async e => {
    e.preventDefault();
    setBroadcasting(true);
    try {
      const { data } = await api.post('/admin/broadcast', broadcast);
      show(`Announcement sent to ${data.notified} user${data.notified !== 1 ? 's' : ''}${data.emailed ? ` (${data.emailed} emailed)` : ''}`);
      setBroadcastModal(false);
      setBroadcast({ title: '', message: '', sendEmail: false });
    } catch (err) {
      show(err.response?.data?.message || 'Broadcast failed', 'error');
    } finally { setBroadcasting(false); }
  };

  // Subscription card: plan/status/dates + the user's payment history
  const openSub = async u => {
    setSubUser(u);
    setSubData(null);
    try {
      const { data } = await api.get(`/admin/users/${u.id}/subscription`);
      setSubData(data);
    } catch (err) {
      show(err.response?.data?.message || 'Could not load subscription', 'error');
      setSubUser(null);
    }
  };

  const del = async u => {
    if (!window.confirm(`Delete ${u.name}? This removes ALL their documents and clients. This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      setUsers(us => us.filter(x => x.id !== u.id));
      show('User deleted');
    } catch (err) {
      show(err.response?.data?.message || 'Delete failed', 'error');
    }
  };

  if (loading) return <Spinner />;

  return (
    <div>
      <ToastContainer />
      <PageHeader title="Users"
        subtitle={filtersActive
          ? `${visible.length} of ${users.length} account${users.length !== 1 ? 's' : ''}`
          : `${users.length} account${users.length !== 1 ? 's' : ''}`}
        actions={
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Btn variant="outline" onClick={() => setBroadcastModal(true)}>📢 Broadcast</Btn>
            <Btn variant="outline" onClick={openExtend}>
              ⏳ Extend Trial{selected.length ? ` (${selected.length})` : ''}
            </Btn>
            <Btn variant="primary" onClick={openNew}>+ Add User</Btn>
          </div>
        } />

      {/* Filter bar */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <Input placeholder="Search name or email…" value={search}
               onChange={e => setSearch(e.target.value)} style={{ flex: 1, minWidth: 200 }} />
        <Select value={filterRole} onChange={e => setFilterRole(e.target.value)} style={{ width: 120 }}>
          <option value="">All Roles</option>
          <option value="admin">Admin</option>
          <option value="user">User</option>
        </Select>
        <Select value={filterPlan} onChange={e => setFilterPlan(e.target.value)} style={{ width: 130 }}>
          <option value="">All Plans</option>
          <option value="trial">Trial</option>
          <option value="monthly">Monthly</option>
          <option value="yearly">Yearly</option>
          <option value="cancelled">Cancelled</option>
        </Select>
        <Select value={filterActive} onChange={e => setFilterActive(e.target.value)} style={{ width: 130 }}>
          <option value="">Active + Inactive</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </Select>
        <Select value={filterVerified} onChange={e => setFilterVerified(e.target.value)} style={{ width: 140 }}>
          <option value="">All Verification</option>
          <option value="true">Verified</option>
          <option value="false">Pending</option>
        </Select>
        {filtersActive && (
          <Btn size="sm" variant="ghost" onClick={clearFilters}>✕ Clear</Btn>
        )}
      </div>

      {users.length === 0
        ? <Empty icon="👥" message="No users yet" />
        : visible.length === 0
        ? <Empty icon="🔍" message="No users match your filters"
            action={<Btn variant="ghost" onClick={clearFilters}>Clear filters</Btn>} />
        : (
          <Card style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', minWidth: 1080, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--bg)', borderBottom: '1px solid var(--border)' }}>
                  <th style={{ ...th, width: 36 }}>
                    <input type="checkbox" checked={visible.length > 0 && visible.every(u => selected.includes(u.id))}
                           onChange={toggleSelectAll} style={{ cursor: 'pointer' }} title="Select all (filtered)" />
                  </th>
                  <SortTh k="name">Name</SortTh>
                  <SortTh k="email">Email</SortTh>
                  <SortTh k="role">Role</SortTh>
                  <th style={th}>Active</th>
                  <th style={th}>Verified</th>
                  <SortTh k="plan">Plan</SortTh>
                  <SortTh k="trialEnds">Trial Ends</SortTh>
                  <SortTh k="registered">Registered</SortTh>
                  <th style={{ ...th, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((u, i) => {
                  const isMe = u.id === me?.id;
                  const planColor = u.plan === 'monthly' || u.plan === 'yearly' ? '#10B981'
                    : u.plan === 'trial' ? '#F59E0B'
                    : '#94a3b8';
                  return (
                    <tr key={u.id} style={{ borderBottom: '1px solid var(--border-light)', background: i % 2 === 0 ? 'var(--card-bg)' : 'var(--hover-bg)', opacity: u.isActive ? 1 : 0.55 }}>
                      <td style={td}>
                        <input type="checkbox" checked={selected.includes(u.id)}
                               onChange={() => toggleSelect(u.id)} style={{ cursor: 'pointer' }} />
                      </td>
                      <td style={td}>
                        <span style={{ fontWeight: 600, color: 'var(--text)' }}>{u.name}</span>
                        {isMe && <span style={{ fontSize: 11, color: 'var(--text-subtle)', marginLeft: 6 }}>(you)</span>}
                      </td>
                      <td style={{ ...td, color: 'var(--text-muted)' }}>{u.email}</td>
                      <td style={td}><Badge label={u.role} color={u.role === 'admin' ? '#F59E0B' : '#64748b'} /></td>
                      <td style={td}><Badge label={u.isActive ? 'active' : 'inactive'} color={u.isActive ? '#10B981' : '#EF4444'} /></td>
                      <td style={td}><Badge label={u.emailVerified ? 'verified' : 'pending'} color={u.emailVerified ? '#10B981' : '#F59E0B'} /></td>
                      <td style={td}><Badge label={u.plan} color={planColor} /></td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}><TrialEnds user={u} onHistory={setHistoryUser} /></td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}><Registered date={u.createdAt} /></td>
                      <td style={{ ...td, textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                          {!u.emailVerified && (
                            <Btn size="sm" variant="success" onClick={() => verify(u)} disabled={busyId === u.id}>✓ Verify</Btn>
                          )}
                          {!isMe && (
                            <Btn size="sm" variant={u.isActive ? 'outline' : 'success'} onClick={() => toggleActive(u)} disabled={busyId === u.id}>
                              {u.isActive ? '⏸ Deactivate' : '▶ Activate'}
                            </Btn>
                          )}
                          {u.role !== 'admin' && (
                            <Btn size="sm" variant="ghost" onClick={() => impersonate(u)} disabled={busyId === u.id}
                              title="Sign in as this user (support)">🕵️ Login as</Btn>
                          )}
                          {u.twoFactorMethod && u.twoFactorMethod !== 'off' && (
                            <Btn size="sm" variant="outline" onClick={() => reset2fa(u)} disabled={busyId === u.id}
                              title={`2FA is on (${u.twoFactorMethod === 'totp' ? 'authenticator app' : 'email codes'}) — turn it off if the user is locked out`}>
                              🔐 Reset 2FA
                            </Btn>
                          )}
                          <Btn size="sm" variant="ghost" onClick={() => openSub(u)} title="Subscription & payment details">💳 Sub</Btn>
                          <Btn size="sm" variant="ghost" onClick={() => openEdit(u)}>Edit</Btn>
                          {!isMe && <Btn size="sm" variant="danger" onClick={() => del(u)}>Del</Btn>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </Card>
        )
      }

      <Modal open={!!historyUser} onClose={() => setHistoryUser(null)}
             title={historyUser ? `Trial Extensions — ${historyUser.name}` : 'Trial Extensions'}>
        {historyUser && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>
              {(historyUser.trialExtensions || []).length} extension{(historyUser.trialExtensions || []).length !== 1 ? 's' : ''},{' '}
              {(historyUser.trialExtensions || []).reduce((s, e) => s + (e.days || 0), 0)} days total.
            </p>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  <th style={{ ...th, padding: '8px 10px' }}>Date</th>
                  <th style={{ ...th, padding: '8px 10px' }}>Days</th>
                  <th style={{ ...th, padding: '8px 10px' }}>By</th>
                  <th style={{ ...th, padding: '8px 10px' }}>New Expiry</th>
                </tr>
              </thead>
              <tbody>
                {[...(historyUser.trialExtensions || [])].reverse().map((e, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border-light)' }}>
                    <td style={{ padding: '8px 10px', color: 'var(--text)' }}>
                      {e.at ? new Date(e.at).toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                    </td>
                    <td style={{ padding: '8px 10px', fontWeight: 700, color: '#2E75B6' }}>+{e.days}</td>
                    <td style={{ padding: '8px 10px', color: 'var(--text-muted)' }}>{e.by || '—'}</td>
                    <td style={{ padding: '8px 10px', color: 'var(--text)' }}>
                      {e.newTrialEndsAt ? new Date(e.newTrialEndsAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Btn variant="ghost" onClick={() => setHistoryUser(null)}>Close</Btn>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={!!subUser} onClose={() => { setSubUser(null); setSubData(null); }} width={680}
             title={subUser ? `Subscription — ${subUser.name}` : 'Subscription'}>
        {!subData ? <Spinner /> : (() => {
          const s = subData.subscription;
          const sum = subData.summary;
          const statusColor = s.status === 'active' ? '#10B981'
            : s.status === 'trialing' ? '#F59E0B'
            : s.status === 'past_due' ? '#EF4444'
            : '#94a3b8';
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '4px 24px' }}>
                <SubRow k="Plan" v={<Badge label={s.plan || '—'} color={s.plan === 'monthly' || s.plan === 'yearly' ? '#10B981' : '#F59E0B'} />} />
                <SubRow k="Status" v={<Badge label={s.status || '—'} color={statusColor} />} />
                <SubRow k="Registered" v={fmtDay(s.registeredAt)} />
                <SubRow k="Subscribed since" v={fmtDay(s.subscribedSince)} />
                {s.plan === 'trial' && <SubRow k="Trial ends" v={fmtDay(s.trialEndsAt)} />}
                <SubRow k={s.status === 'cancelled' ? 'Access ends' : 'Next billing'} v={fmtDay(s.currentPeriodEnd)} />
                {s.pastDueSince && <SubRow k="Past due since" v={<span style={{ color: '#EF4444', fontWeight: 600 }}>{fmtDay(s.pastDueSince)}</span>} />}
                <SubRow k="Total paid" v={<b>{sum.totalPaid.length ? sum.totalPaid.map(t => `${fmtAmt(t.total, t.currency)} (${t.count}×)`).join(' · ') : '—'}</b>} />
                <SubRow k="Last payment" v={sum.lastPayment ? `${fmtAmt(sum.lastPayment.amount, sum.lastPayment.currency)} on ${fmtDay(sum.lastPayment.paidAt)}` : '—'} />
                {sum.failedCount > 0 && <SubRow k="Failed payments" v={<span style={{ color: '#EF4444', fontWeight: 600 }}>{sum.failedCount}</span>} />}
              </div>
              {s.stripeCustomerId && (
                <div style={{ fontSize: 11, color: 'var(--text-subtle)' }}>
                  Stripe: {s.stripeCustomerId}{s.stripeSubscriptionId ? ` · ${s.stripeSubscriptionId}` : ''}
                </div>
              )}

              <div>
                <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-muted)', marginBottom: 8 }}>
                  Payment history
                </div>
                {subData.payments.length === 0
                  ? <p style={{ fontSize: 13, color: 'var(--text-subtle)', margin: 0 }}>No payments recorded for this user.</p>
                  : (
                    <div style={{ overflowX: 'auto', maxHeight: 300, overflowY: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid var(--border)' }}>
                            {['Date', 'Amount', 'Plan', 'Status', 'Invoice', ''].map(h => (
                              <th key={h} style={{ ...th, padding: '6px 10px' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {subData.payments.map(p => (
                            <tr key={p._id} style={{ borderBottom: '1px solid var(--border-light)' }}>
                              <td style={subTd}>{fmtDay(p.paidAt)}</td>
                              <td style={{ ...subTd, fontWeight: 600, whiteSpace: 'nowrap', color: p.status === 'succeeded' ? '#10B981' : '#EF4444' }}>{fmtAmt(p.amount, p.currency)}</td>
                              <td style={subTd}>{p.plan || '—'}</td>
                              <td style={subTd}><Badge label={p.status} color={p.status === 'succeeded' ? '#10B981' : '#EF4444'} /></td>
                              <td style={subTd}>{p.invoiceNumber || '—'}</td>
                              <td style={subTd}>
                                {p.receiptUrl && <a href={p.receiptUrl} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#2E75B6' }}>Receipt ↗</a>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Btn variant="ghost" onClick={() => { setSubUser(null); setSubData(null); }}>Close</Btn>
              </div>
            </div>
          );
        })()}
      </Modal>

      <Modal open={broadcastModal} onClose={() => setBroadcastModal(false)} title="Broadcast Announcement">
        <form onSubmit={sendBroadcast} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>
            Every active user gets an in-app notification in their bell menu. Tick the checkbox to also
            email everyone (sent via BCC — users never see each other's addresses).
          </p>
          <Input label="Title *" value={broadcast.title}
                 onChange={e => setBroadcast(b => ({ ...b, title: e.target.value }))}
                 required placeholder="Scheduled maintenance / New feature…" />
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 }}>Message *</div>
            <textarea value={broadcast.message} required rows={5}
              onChange={e => setBroadcast(b => ({ ...b, message: e.target.value }))}
              placeholder="What do you want to tell your users?"
              style={{
                width: '100%', padding: '10px 12px', fontSize: 13, fontFamily: 'inherit',
                border: '1px solid var(--border)', borderRadius: 8, resize: 'vertical',
                background: 'var(--input-bg)', color: 'var(--text)', outline: 'none',
              }} />
          </div>
          <label style={chkLabel}>
            <input type="checkbox" checked={broadcast.sendEmail}
                   onChange={e => setBroadcast(b => ({ ...b, sendEmail: e.target.checked }))} />
            <span>Also send as an email to all users</span>
          </label>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <Btn variant="ghost" type="button" onClick={() => setBroadcastModal(false)}>Cancel</Btn>
            <Btn variant="primary" type="submit" disabled={broadcasting}>
              {broadcasting ? 'Sending…' : '📢 Send Announcement'}
            </Btn>
          </div>
        </form>
      </Modal>

      <Modal open={extendModal} onClose={() => setExtendModal(false)} title="Extend Trial">
        <form onSubmit={extendTrial} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Select label="Apply to" value={extendScope} onChange={e => setExtendScope(e.target.value)}>
            <option value="selected" disabled={!selected.length}>
              Selected users ({selected.length})
            </option>
            <option value="all">All trial users</option>
          </Select>
          <Input label="Extend by (days)" type="number" min="1" max="365" value={extendDays}
                 onChange={e => setExtendDays(e.target.value)} required />
          <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>
            Active trials get the days added to their current expiry. Expired or cancelled trials
            restart from today and are set back to trialing. Paying subscribers are not affected.
          </p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <Btn variant="ghost" type="button" onClick={() => setExtendModal(false)}>Cancel</Btn>
            <Btn variant="primary" type="submit" disabled={extending || (extendScope === 'selected' && !selected.length)}>
              {extending ? 'Extending…' : 'Extend Trial'}
            </Btn>
          </div>
        </form>
      </Modal>

      <Modal open={modal} onClose={() => setModal(false)} title={editing ? 'Edit User' : 'Add User'}>
        <form onSubmit={save} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Input label="Full Name *" value={form.name} onChange={set('name')} required />
          <Input label="Email *" type="email" value={form.email} onChange={set('email')} required />
          <div>
            <Input label={editing ? 'New Password (leave blank to keep)' : 'Password *'} type="password"
                   value={form.password || ''} onChange={set('password')} required={!editing} />
            <PasswordStrength password={form.password || ''} show={!!form.password || !editing} />
          </div>
          <Select label="Role" value={form.role} onChange={set('role')}>
            <option value="user">User</option>
            <option value="admin">Admin</option>
          </Select>

          {editing && (
            <>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <label style={chkLabel}>
                  <input type="checkbox" checked={!!form.isActive} onChange={setBool('isActive')} disabled={form.id === me?.id} />
                  <span>Account active</span>
                </label>
                <label style={chkLabel}>
                  <input type="checkbox" checked={!!form.emailVerified} onChange={setBool('emailVerified')} />
                  <span>Email verified</span>
                </label>
              </div>
              <Select label="Plan" value={form.plan || 'trial'} onChange={set('plan')}>
                <option value="trial">Trial</option>
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
                <option value="cancelled">Cancelled</option>
              </Select>
              <Select label="Subscription Status" value={form.subscriptionStatus || 'trialing'} onChange={set('subscriptionStatus')}>
                <option value="trialing">Trialing</option>
                <option value="active">Active</option>
                <option value="past_due">Past Due</option>
                <option value="cancelled">Cancelled</option>
                <option value="unpaid">Unpaid</option>
                <option value="incomplete">Incomplete</option>
              </Select>
            </>
          )}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <Btn variant="ghost" type="button" onClick={() => setModal(false)}>Cancel</Btn>
            <Btn variant="primary" type="submit" disabled={saving}>{saving ? 'Saving…' : editing ? 'Save' : 'Create'}</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}

const fmtAmt = (amount, currency) =>
  `${String(currency || 'myr').toUpperCase()} ${Number(amount || 0).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDay = d => (d ? new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const subTd = { padding: '7px 10px', color: 'var(--text)', verticalAlign: 'middle' };

function SubRow({ k, v }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, fontSize: 13, padding: '6px 0', borderBottom: '1px solid var(--border-light)' }}>
      <span style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{k}</span>
      <span style={{ color: 'var(--text)', textAlign: 'right' }}>{v}</span>
    </div>
  );
}

function Registered({ date }) {
  if (!date) return <span style={{ color: 'var(--text-subtle)' }}>—</span>;
  const d = new Date(date);
  const ageDays = Math.floor((Date.now() - d) / (24 * 60 * 60 * 1000));
  const ago = ageDays === 0 ? 'today'
    : ageDays < 30 ? `${ageDays} day${ageDays !== 1 ? 's' : ''} ago`
    : ageDays < 365 ? `${Math.floor(ageDays / 30)} month${Math.floor(ageDays / 30) !== 1 ? 's' : ''} ago`
    : `${Math.floor(ageDays / 365)} year${Math.floor(ageDays / 365) !== 1 ? 's' : ''} ago`;
  return (
    <div title={d.toLocaleString()}>
      <div style={{ color: 'var(--text)' }}>{d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}</div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{ago}</div>
    </div>
  );
}

function TrialEnds({ user: u, onHistory }) {
  const exts = u.trialExtensions || [];
  const totalExtended = exts.reduce((s, e) => s + (e.days || 0), 0);
  const badge = totalExtended > 0 && (
    <button onClick={() => onHistory(u)}
      title={`Extended ${exts.length} time${exts.length !== 1 ? 's' : ''} — click for history`}
      style={{
        background: 'rgba(46,117,182,0.12)', color: '#2E75B6', border: '1px solid rgba(46,117,182,0.35)',
        borderRadius: 20, padding: '1px 8px', fontSize: 11, fontWeight: 700, cursor: 'pointer',
        marginInlineStart: 6, verticalAlign: 'middle',
      }}>
      +{totalExtended}d
    </button>
  );
  if (u.plan !== 'trial' || !u.trialEndsAt) {
    return <span style={{ color: 'var(--text-subtle)' }}>—{badge}</span>;
  }
  const ends = new Date(u.trialEndsAt);
  const daysLeft = Math.ceil((ends - Date.now()) / (24 * 60 * 60 * 1000));
  const expired = daysLeft <= 0;
  const dateStr = ends.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  return (
    <div>
      <div style={{ color: 'var(--text)' }}>{dateStr}{badge}</div>
      <div style={{ fontSize: 12, fontWeight: 600, color: expired ? '#EF4444' : daysLeft <= 7 ? '#F59E0B' : 'var(--text-muted)' }}>
        {expired ? 'Expired' : `${daysLeft} day${daysLeft !== 1 ? 's' : ''} left`}
      </div>
    </div>
  );
}

const th = { padding: '12px 16px', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' };
const td = { padding: '13px 16px', fontSize: 14, color: 'var(--text)', verticalAlign: 'middle' };
const chkLabel = { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-muted)', cursor: 'pointer', userSelect: 'none' };
